// /admin/integrations/kiosk — config kiosk mode (Sprint 7).

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

export function KioskPage() {
  const qc = useQueryClient();
  const settings = useQuery({
    queryKey: ['admin', 'kiosk-settings'],
    queryFn: api.getKioskSettings,
  });

  const [enabled, setEnabled] = useState(false);
  const [welcome, setWelcome] = useState('');
  const [resetSec, setResetSec] = useState(30);
  const [pin, setPin] = useState('');
  const [pinTouched, setPinTouched] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (settings.data) {
      setEnabled(settings.data.kiosk_mode_enabled);
      setWelcome(settings.data.kiosk_welcome_message ?? '');
      setResetSec(settings.data.kiosk_idle_reset_seconds);
    }
  }, [settings.data]);

  const saveMu = useMutation({
    mutationFn: () =>
      api.updateKioskSettings({
        kiosk_mode_enabled: enabled,
        kiosk_idle_reset_seconds: resetSec,
        kiosk_welcome_message: welcome.trim() || null,
        ...(pinTouched ? { kiosk_exit_pin: pin === '' ? null : pin } : {}),
      }),
    onSuccess: () => {
      setSuccess('Salvato.');
      setTimeout(() => setSuccess(null), 3000);
      setPin('');
      setPinTouched(false);
      qc.invalidateQueries({ queryKey: ['admin', 'kiosk-settings'] });
    },
  });

  if (settings.isLoading) {
    return (
      <p className="mono" style={{ color: 'var(--mute)' }}>
        Caricamento…
      </p>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div>
        <h3 className="serif text-2xl mb-3" style={{ color: 'var(--ink)' }}>
          Modalità kiosk
        </h3>

        <label className="flex items-center gap-3 mb-4 text-sm" style={{ color: 'var(--ink)' }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Abilita modalità kiosk per questa installazione
        </label>

        <label className="block mb-4 text-sm" style={{ color: 'var(--ink)' }}>
          PIN uscita (4-8 cifre)
          <input
            type="password"
            inputMode="numeric"
            maxLength={8}
            value={pin}
            placeholder={
              settings.data?.pin_configured
                ? 'configurato — lascia vuoto per non cambiare'
                : 'es. 1234'
            }
            onChange={(e) => {
              setPin(e.target.value.replace(/\D/g, ''));
              setPinTouched(true);
            }}
            className="mt-1 w-full rounded-md border px-3 py-2"
            style={{
              background: 'var(--surface)',
              borderColor: 'var(--line)',
              color: 'var(--ink)',
            }}
            autoComplete="off"
          />
          <span className="text-xs mt-1 inline-block" style={{ color: 'var(--mute)' }}>
            {settings.data?.pin_configured
              ? 'PIN configurato. Lascia vuoto per non modificarlo.'
              : 'Nessun PIN configurato. Necessario per uscire dalla modalità kiosk.'}
          </span>
        </label>

        <label className="block mb-4 text-sm" style={{ color: 'var(--ink)' }}>
          Auto-reset (secondi inattività post-completamento)
          <input
            type="number"
            min={10}
            max={120}
            value={resetSec}
            onChange={(e) => setResetSec(Number(e.target.value))}
            className="mt-1 w-full rounded-md border px-3 py-2"
            style={{
              background: 'var(--surface)',
              borderColor: 'var(--line)',
              color: 'var(--ink)',
            }}
          />
        </label>

        <label className="block mb-4 text-sm" style={{ color: 'var(--ink)' }}>
          Welcome message (max 280 char)
          <textarea
            value={welcome}
            onChange={(e) => setWelcome(e.target.value)}
            maxLength={280}
            rows={3}
            placeholder="Benvenuto. Tocca per iniziare."
            className="mt-1 w-full rounded-md border px-3 py-2"
            style={{
              background: 'var(--surface)',
              borderColor: 'var(--line)',
              color: 'var(--ink)',
            }}
          />
        </label>

        <button
          onClick={() => saveMu.mutate()}
          disabled={saveMu.isPending}
          className="rounded-md px-5 py-2 text-sm font-medium"
          style={{
            background: 'var(--primary)',
            color: '#fff',
            opacity: saveMu.isPending ? 0.6 : 1,
          }}
        >
          {saveMu.isPending ? 'Salvataggio…' : 'Salva configurazione'}
        </button>

        {saveMu.error && (
          <p className="mt-3 text-sm" style={{ color: 'var(--danger)' }}>
            {String(saveMu.error)}
          </p>
        )}
        {success && (
          <p className="mt-3 text-sm" style={{ color: 'var(--primary-2)' }}>
            {success}
          </p>
        )}

        <div className="mt-6 text-xs" style={{ color: 'var(--mute)' }}>
          Override URL test: <code className="mono">/chat/?kiosk=true</code> o{' '}
          <code className="mono">/chat/?kiosk=false</code>
        </div>
      </div>

      <div>
        <h3 className="serif text-2xl mb-3" style={{ color: 'var(--ink)' }}>
          Anteprima
        </h3>
        <div
          className="rounded-md border p-8 text-center"
          style={{ background: 'var(--surface)', borderColor: 'var(--line)' }}
        >
          <div
            className="mx-auto mb-4 rounded-full"
            style={{
              width: 96,
              height: 96,
              background: 'var(--primary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 64,
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 700,
              color: '#fff',
            }}
          >
            A
          </div>
          <h4 className="serif text-2xl mb-2" style={{ color: 'var(--ink)' }}>
            {welcome.trim() || 'Benvenuto. Tocca per iniziare.'}
          </h4>
          <p className="mono text-xs" style={{ color: 'var(--mute)' }}>
            Tocca lo schermo
          </p>
        </div>
      </div>
    </div>
  );
}
